(() => {
  const localeRoot = () => window.Shopify?.routes?.root || '/';
  const previewId = () => new URL(window.location.href).searchParams.get('preview_theme_id');
  const cartPath = () => window.routes?.cart_url || `${localeRoot()}cart`;
  const relativeUrl = (url) => `${url.pathname}${url.search}${url.hash}`;
  const errorText = () => window.cartStrings?.error || 'There was an error while updating your cart. Please try again.';
  const plainClick = (event) => !event.defaultPrevented && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey;

  const viewCartUrl = () => {
    const url = new URL(cartPath(), window.location.origin);
    url.searchParams.set('view', 'page');
    if (previewId()) url.searchParams.set('preview_theme_id', previewId());
    return relativeUrl(url);
  };

  const prepareViewBagLinks = (root) => {
    const locale = (document.documentElement.lang || 'en').split('-')[0].toLowerCase();
    const labels = { tr: 'Sepeti görüntüle', de: 'Warenkorb ansehen', fr: 'Voir le panier', es: 'Ver cesta', it: 'Vedi carrello' };
    root.querySelectorAll('.joffz-cart__continue').forEach((link) => {
      const label = labels[locale] || 'View bag';
      if (link.textContent.trim() !== label) link.textContent = label;
      if (link.getAttribute('href') !== viewCartUrl()) link.setAttribute('href', viewCartUrl());
      link.dataset.cartPageLink = 'true';
    });
  };

  class CartDrawer extends HTMLElement {
    get joffzCartVersion() { return 60; }

    constructor() {
      super();
      this.contentVersion = 0;
      this.changePending = false;
      this.refreshAfterChange = false;
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.addEventListener('click', (event) => {
        if (event.target instanceof Element && event.target.closest('#CartDrawer-Overlay')) this.close();
      });
    }

    connectedCallback() {
      this.dataset.joffzCartVersion = '60';
      this.inert = !this.classList.contains('active');
      this.setAttribute('aria-hidden', String(this.inert));
      this.setHeaderCartIconAccessibility();
      this.syncState();
    }

    disconnectedCallback() {
      this.invalidateRefresh();
    }

    setHeaderCartIconAccessibility() {
      const link = document.getElementById('cart-icon-bubble');
      if (!link || link.dataset.joffzCartV60 === 'true') return;
      link.dataset.joffzCartV60 = 'true';
      link.setAttribute('role', 'button');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-controls', 'CartDrawer');
      link.addEventListener('click', (event) => {
        if (!plainClick(event)) return;
        event.preventDefault();
        document.querySelector('cart-drawer')?.open(link);
      });
      link.addEventListener('keydown', (event) => {
        if (event.code !== 'Space') return;
        event.preventDefault();
        document.querySelector('cart-drawer')?.open(link);
      });
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    focusDialog(preferredId) {
      if (!this.classList.contains('active')) return;
      const dialog = this.querySelector('.drawer__inner');
      const preferred = preferredId ? this.querySelector(`[id="${CSS.escape(preferredId)}"]`) : null;
      const target = preferred || this.querySelector('.drawer__close') || dialog;
      if (dialog && target && typeof trapFocus === 'function') trapFocus(dialog, target);
    }

    open(triggeredBy, refresh = true) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      prepareViewBagLinks(this);
      this.inert = false;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
      requestAnimationFrame(() => this.focusDialog());
      if (refresh) this.refreshContents();
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      this.inert = true;
      document.body.classList.remove('overflow-hidden');
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      if (typeof html !== 'string' || !html.trim()) return null;
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return this.getSectionDOM(html, selector)?.innerHTML ?? null;
    }

    invalidateRefresh() {
      this.contentVersion += 1;
      this.refreshController?.abort();
      this.refreshController = null;
      return this.contentVersion;
    }

    syncState() {
      const rows = this.querySelectorAll('.drawer__cart-items-wrapper .cart-item');
      const empty = rows.length === 0;
      this.classList.toggle('is-empty', empty);
      this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', empty);
      const footer = this.querySelector('.drawer__footer');
      if (footer) footer.hidden = empty;
      const emptyPanel = this.querySelector('.drawer__inner-empty');
      if (emptyPanel) emptyPanel.style.setProperty('display', empty ? 'flex' : 'none', 'important');
      const checkout = this.querySelector('#CartDrawer-Checkout');
      if (checkout) checkout.disabled = empty || this.changePending;
      prepareViewBagLinks(this);
    }

    applySections(sections, expectedCount) {
      const source = this.getSectionDOM(sections?.['cart-drawer'], 'cart-drawer #CartDrawer');
      const target = this.querySelector('#CartDrawer');
      if (!source || !target || !source.querySelector('.drawer__inner') || !source.querySelector('cart-drawer-items')) return false;
      if (source.querySelector('cart-items, #main-cart-items, .page-header')) return false;
      const countElement = source.querySelector('.drawer__heading-count');
      const count = countElement ? Number(countElement.textContent.trim()) : NaN;
      const rows = source.querySelectorAll('.drawer__cart-items-wrapper .cart-item');
      if (!Number.isInteger(count) || count < 0 || (count === 0) !== (rows.length === 0)) return false;
      if (Number.isInteger(expectedCount) && expectedCount !== count) return false;
      if (count === 0 && !source.querySelector('.drawer__inner-empty')) return false;
      const focusedId = this.contains(document.activeElement) ? document.activeElement.id : null;
      const scrollTop = this.querySelector('cart-drawer-items')?.scrollTop || 0;
      target.innerHTML = source.innerHTML;
      const items = this.querySelector('cart-drawer-items');
      if (items) items.scrollTop = scrollTop;
      this.syncState();
      const bubble = this.getSectionDOM(sections?.['cart-icon-bubble']);
      const bubbleTarget = document.getElementById('cart-icon-bubble');
      if (bubble && bubbleTarget) bubbleTarget.innerHTML = bubble.innerHTML;
      this.focusDialog(focusedId);
      return true;
    }

    renderContents(state) {
      this.invalidateRefresh();
      this.productId = state?.id;
      if (this.changePending) {
        this.refreshAfterChange = true;
      } else {
        this.applySections(state?.sections, state?.item_count);
      }
      this.open(null, false);
      this.refreshContents();
    }

    async refreshContents() {
      if (!this.isConnected) return false;
      if (this.changePending) {
        this.refreshAfterChange = true;
        return false;
      }
      const version = this.invalidateRefresh();
      const controller = new AbortController();
      this.refreshController = controller;
      const url = new URL(localeRoot(), window.location.origin);
      url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
      if (previewId()) url.searchParams.set('preview_theme_id', previewId());
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(relativeUrl(url), {
          credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal
        });
        if (!response.ok) throw new Error(`Cart sections HTTP ${response.status}`);
        const sections = await response.json();
        if (version !== this.contentVersion || !this.isConnected || this.changePending) return false;
        if (!this.applySections(sections)) throw new Error('Invalid cart-drawer section');
        return true;
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.contentVersion && this.isConnected) this.showError(errorText());
        return false;
      } finally {
        if (version === this.contentVersion) {
          this.refreshController = null;
          this.setAttribute('aria-busy', String(this.changePending));
        }
      }
    }

    showError(message, line) {
      const local = line ? this.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`) : null;
      const target = local || this.querySelector('#CartDrawer-CartErrors');
      if (target) target.textContent = String(message || '');
    }

    setChangeBusy(busy, line) {
      this.setAttribute('aria-busy', String(busy));
      this.querySelector('#CartDrawer-CartItems')?.classList.toggle('cart__items--disabled', busy);
      this.querySelectorAll('cart-drawer-items button, cart-drawer-items input[name="updates[]"]').forEach((control) => {
        if (busy && !control.disabled) {
          control.dataset.joffzV60Disabled = 'true';
          control.disabled = true;
        } else if (!busy && control.dataset.joffzV60Disabled === 'true') {
          control.disabled = false;
          delete control.dataset.joffzV60Disabled;
        }
      });
      this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`).forEach((overlay) => overlay.classList.toggle('hidden', !busy));
      this.querySelector('#CartDrawer-LineItemStatus')?.setAttribute('aria-hidden', String(!busy));
      this.syncState();
    }

    async changeQuantity(lineValue, quantityValue) {
      const line = Number(lineValue);
      const quantity = Number(quantityValue);
      if (this.changePending || !Number.isInteger(line) || line < 1 || !Number.isInteger(quantity) || quantity < 0) return;
      const input = this.querySelector(`#Drawer-quantity-${line}`);
      if (!input) return;
      const previousValue = input.getAttribute('value');
      const variantId = input.dataset.variantId;
      const version = this.invalidateRefresh();
      this.changePending = true;
      this.refreshAfterChange = false;
      this.showError('', line);
      this.showError('');
      this.setChangeBusy(true, line);
      try {
        const response = await fetch(window.routes?.cart_change_url || `${localeRoot()}cart/change.js`, {
          method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.getSectionsToRender().map((section) => section.id), sections_url: `${window.location.pathname}${window.location.search}` })
        });
        const state = await response.json();
        if (!response.ok || state.errors || state.status) throw new Error(typeof state.errors === 'string' ? state.errors : state.description || state.message || errorText());
        if (!Number.isInteger(state.item_count) || !Array.isArray(state.items)) throw new Error(errorText());
        if (!this.isConnected) return;
        if (version !== this.contentVersion || !this.applySections(state.sections, state.item_count)) this.refreshAfterChange = true;
        document.dispatchEvent(new CustomEvent('line-item:change', { detail: { lineItem: state.items[line - 1] || null, cart: state, sectionId: 'CartDrawer' } }));
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: state, sectionId: 'CartDrawer' } }));
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-items', cartData: state });
      } catch (error) {
        if (input.isConnected) input.value = previousValue;
        this.showError(error.message || errorText(), line);
        document.dispatchEvent(new CustomEvent('cart:error', { detail: { source: 'CartDrawer', productVariantId: variantId, errors: error.message || errorText(), message: error.message || errorText() } }));
      } finally {
        this.changePending = false;
        if (this.isConnected) {
          this.setChangeBusy(false, line);
          if (this.refreshAfterChange) {
            this.refreshAfterChange = false;
            this.refreshContents();
          }
        }
      }
    }
  }

  class CartDrawerItems extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'updates[]') return;
        clearTimeout(this.changeTimer);
        this.changeTimer = setTimeout(() => {
          if (this.isConnected && this.contains(input)) this.updateQuantity(input.dataset.index, input.value);
        }, typeof ON_CHANGE_DEBOUNCE_TIMER === 'number' ? ON_CHANGE_DEBOUNCE_TIMER : 300);
      });
    }

    connectedCallback() {
      this.cartUpdateUnsubscriber?.();
      if (typeof subscribe !== 'function') return;
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'cart-drawer-items' || event?.source === 'product-form') return;
        this.onCartUpdate();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.changeTimer);
      this.cartUpdateUnsubscriber?.();
      this.cartUpdateUnsubscriber = undefined;
    }

    onCartUpdate() {
      return this.closest('cart-drawer')?.refreshContents();
    }

    getSectionsToRender() {
      return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }, { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
    }

    updateQuantity(line, quantity) {
      return this.closest('cart-drawer')?.changeQuantity(line, quantity);
    }
  }

  if (!document.getElementById('joffz-cart-fix-styles')) {
    const style = document.createElement('style');
    style.id = 'joffz-cart-fix-styles';
    style.textContent = `
      .cart-drawer .cart-item__save-badge { display: none !important; }
      .cart-drawer .cart-item__discounted-prices { display: flex; align-items: baseline; flex-wrap: wrap; column-gap: 0.6rem; }
      .cart-drawer .cart-item__discounted-prices > dd:last-of-type { order: 1; }
      .cart-drawer .cart-item__discounted-prices > dd.price { order: 2; font-weight: 700; }
      .cart-drawer .cart-item__old-price { color: #c0392b !important; text-decoration: line-through !important; text-decoration-color: #c0392b !important; opacity: 0.85; }
      .price--on-sale .price__compare .price-item--regular { color: #c0392b !important; text-decoration-color: #c0392b !important; opacity: 0.85; }
    `;
    document.head.appendChild(style);
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

  const boot = () => {
    const drawer = document.querySelector('cart-drawer');
    drawer?.setHeaderCartIconAccessibility();
    if (drawer) prepareViewBagLinks(drawer);
    const url = new URL(window.location.href);
    if (drawer && url.searchParams.get('open_cart') === '1') {
      drawer.open(document.getElementById('cart-icon-bubble'));
      url.searchParams.delete('open_cart');
      window.history.replaceState({}, '', relativeUrl(url));
    }
  };

  if (!window.__joffzCartV60Links) {
    window.__joffzCartV60Links = true;
    document.addEventListener('click', (event) => {
      if (!plainClick(event) || !(event.target instanceof Element)) return;
      const link = event.target.closest('a[href]');
      if (!link || link.closest('form') || link.hasAttribute('data-cart-page-link') || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      const expected = new URL(cartPath(), window.location.origin);
      if (url.origin !== expected.origin || url.pathname.replace(/\/$/, '') !== expected.pathname.replace(/\/$/, '') || url.searchParams.get('view') === 'page') return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      event.preventDefault();
      drawer.open(link);
    });
    document.addEventListener('shopify:section:load', boot);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) document.querySelector('cart-drawer')?.refreshContents();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
